import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';

export interface EditableTextBlockProps {
  pageId: string;
  blockId: string;
  heading?: string;
  body: string;
  onSave?: (body: string, heading?: string) => void | Promise<void>;
}

const EditableTextBlock: React.FC<EditableTextBlockProps> = ({ pageId, blockId, heading, body, onSave }) => {
  const { canEditTexts, hasPermission } = useAuth();
  const { darkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [editedHeading, setEditedHeading] = useState(heading || '');
  const [editedBody, setEditedBody] = useState(body);

  const moduleMatch = pageId.match(/^m(\d+)_/i);
  const moduleKey = moduleMatch ? `m${moduleMatch[1]}` : null;
  const modulePermission = moduleKey ? `edit_texts_${moduleKey}` : null;
  const canEdit = modulePermission ? hasPermission(modulePermission) : canEditTexts;

  // Sync eingehende Props wenn sich Override aktualisiert und Dialog nicht offen ist
  useEffect(() => {
    if (!open) {
      setEditedHeading(heading || '');
      setEditedBody(body);
    }
  }, [heading, body, open]);

  const handleOpen = useCallback(() => {
    if (canEdit) {
      setOpen(true);
    }
  }, [canEdit]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setEditedHeading(heading || '');
    setEditedBody(body);
  }, [heading, body]);

  const handleSave = useCallback(async () => {
    if (onSave) {
      try {
        await onSave(editedBody, heading !== undefined ? editedHeading : undefined);
      } catch (e) {
        console.error('Save failed:', e);
        alert('Speichern fehlgeschlagen: ' + (e instanceof Error ? e.message : e));
        return;
      }
    } else {
      // Fallback: nur console log wenn kein onSave callback
      console.log('Save:', { pageId, blockId, heading: editedHeading, body: editedBody });
    }
    setOpen(false);
  }, [pageId, blockId, editedHeading, editedBody, onSave, heading]);

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          cursor: canEdit ? 'pointer' : 'default',
          '&:hover .edit-overlay': canEdit ? { opacity: 1 } : {}
        }}
        onClick={handleOpen}
      >
        {heading && (
          <Typography
            variant="subtitle1"
            sx={{
              color: tokens.colors.primary,
              fontWeight: 600,
              mb: 0.5
            }}
          >
            {heading}
          </Typography>
        )}
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text
          }}
        >
          {body}
        </Typography>

        {canEdit && (
          <Box
            className="edit-overlay"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              background: 'rgba(178, 20, 15, 0.9)',
              color: '#fff',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: 12,
              opacity: 0,
              transition: 'opacity 0.2s',
              pointerEvents: 'none'
            }}
          >
            ✏️ Bearbeiten
          </Box>
        )}
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Text bearbeiten</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {heading !== undefined && (
            <TextField
              fullWidth
              label="Überschrift"
              value={editedHeading}
              onChange={(e) => setEditedHeading(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={8}
            label="Text"
            value={editedBody}
            onChange={(e) => setEditedBody(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditableTextBlock;
